import React from "react";
import { Box, Button, LinearProgress } from "@mui/material";
import { IUser } from "../../../api/homesites/homesites.types";
import { Community } from "../../../api/communities/types";
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";
import { useDivisionFeatureFlag } from "../../../stateContext/featureFlags/FeatureFlagProvider";
import { useBulkAssignData } from "./hooks/useBulkAssignData";
import { useBulkAssignConcierge } from "./hooks/useBulkAssignConcierge";
import { BulkAssignConciergeDialog } from "./components/BulkAssignConciergeDialog";
import { UnassignConfirmDialog } from "./components/UnassignConfirmDialog";
import { AssignConfirmDialog } from "./components/AssignConfirmDialog";
import { ReassignDialog } from "./components/ReassignDialog";

interface BulkAssignConciergeProps {
  userInfo: any;
  homesites: HomeSitesDataIndex[];
  communities?: Community[];
  conciergesWithSelf: IUser[];
  isConcierge: boolean;
  currentCommunity?: string;
}

export const BulkAssignConcierge: React.FC<BulkAssignConciergeProps> = ({
  userInfo,
  homesites,
  communities = [],
  conciergesWithSelf,
  isConcierge,
  currentCommunity = "",
}) => {
  const bulkConciergeAssignmentEnabled = useDivisionFeatureFlag(userInfo?.userDivCode || "", "bulkConciergeAssignment");

  // Get state and handlers from hook
  const {
    bulkAssignDialogOpen,
    selectedBulkConcierge,
    selectedBulkCommunity,
    isBulkAssigning,
    bulkAssignProgress,
    unassignConfirmDialogOpen,
    communityToUnassign,
    assignConfirmDialogOpen,
    reassignDialogOpen,
    communityToReassign,
    targetConcierge,
    hasChanges,
    setTargetConcierge,
    handleCommunityChange,
    handleBulkAssignClick,
    handleUnassignClick,
    handleUnassignConfirm,
    handleUnassignCancel,
    handleReassignClick,
    handleReassignCancel,
    handleReassignConfirm,
    handleBulkAssignConfirm,
    handleBulkAssignExecute,
    handleAssignConfirmCancel,
    handleBulkAssignCancel,
    handleDialogClose,
    handleConciergeChange,
  } = useBulkAssignConcierge({
    userInfo,
    homesites,
    communities,
    conciergesWithSelf,
    currentCommunity,
  });

  // Get data calculations - needs to be called after hook to get currentUserAsConcierge
  const {
    currentUserAsConcierge,
    sortedConcierges,
    availableCommunities,
    hasHomesitesToUpdate,
    communityConciergeBreakdown,
    conciergeCommunities,
  } = useBulkAssignData({
    homesites,
    communities,
    conciergesWithSelf,
    userInfo,
    selectedBulkConcierge,
    selectedBulkCommunity,
  });

  if (!bulkConciergeAssignmentEnabled) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleBulkAssignClick}
        disabled={isBulkAssigning}
        style={{ textTransform: "none" }}
      >
        Community Level Assignment
      </Button>

      {isBulkAssigning && (
        <Box sx={{ width: "100%", marginBottom: "8px", marginTop: "8px" }}>
          <LinearProgress
            variant="determinate"
            value={(bulkAssignProgress.current / bulkAssignProgress.total) * 100}
          />
          <div style={{ textAlign: "center", marginTop: "4px", fontSize: "12px" }}>
            Updating {bulkAssignProgress.current} of {bulkAssignProgress.total} (community + homesites)...
          </div>
        </Box>
      )}

      <BulkAssignConciergeDialog
        open={bulkAssignDialogOpen}
        selectedBulkConcierge={selectedBulkConcierge}
        selectedBulkCommunity={selectedBulkCommunity}
        availableCommunities={availableCommunities}
        sortedConcierges={sortedConcierges}
        communityConciergeBreakdown={communityConciergeBreakdown}
        conciergeCommunities={conciergeCommunities}
        hasHomesitesToUpdate={hasHomesitesToUpdate}
        hasChanges={hasChanges}
        isBulkAssigning={isBulkAssigning}
        userInfo={userInfo}
        onClose={handleDialogClose}
        onConciergeChange={handleConciergeChange}
        onCommunityChange={handleCommunityChange}
        onConfirm={handleBulkAssignConfirm}
        onCancel={handleBulkAssignCancel}
        onReassignClick={handleReassignClick}
        onUnassignClick={handleUnassignClick}
      />

      <UnassignConfirmDialog
        open={unassignConfirmDialogOpen}
        selectedBulkConcierge={selectedBulkConcierge}
        communityToUnassign={communityToUnassign}
        homesites={homesites}
        isBulkAssigning={isBulkAssigning}
        onConfirm={handleUnassignConfirm}
        onCancel={handleUnassignCancel}
      />

      <AssignConfirmDialog
        open={assignConfirmDialogOpen}
        selectedBulkConcierge={selectedBulkConcierge}
        selectedBulkCommunity={selectedBulkCommunity}
        homesites={homesites}
        isBulkAssigning={isBulkAssigning}
        onConfirm={handleBulkAssignExecute}
        onCancel={handleAssignConfirmCancel}
      />

      <ReassignDialog
        open={reassignDialogOpen}
        selectedBulkConcierge={selectedBulkConcierge}
        communityToReassign={communityToReassign}
        targetConcierge={targetConcierge}
        homesites={homesites}
        sortedConcierges={sortedConcierges}
        userInfo={userInfo}
        isBulkAssigning={isBulkAssigning}
        onConfirm={handleReassignConfirm}
        onCancel={handleReassignCancel}
        onTargetConciergeChange={setTargetConcierge}
      />
    </>
  );
};
